import { useImperativeHandle, useRef, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import type { RMAvailableDirectory, RMReview } from '@brightlocal/api-client';
import { Button } from '@brightlocal/ui-components/button';
import { CardToolbar } from '@brightlocal/ui-components/card';
import { DataTableSearch, type TanStackTable } from '@brightlocal/ui-components/data-table';
import { MultiSelectFilter } from '@brightlocal/ui-components/multi-select-filter';

import {
  PERIOD_LABEL_KEYS,
  REVIEW_PERIOD,
  SingleSelectFilter,
  useFilterTriggerSize,
  type ReviewPeriod
} from 'shared/modules/reviews';

import {
  REVIEW_MANAGER_FILTER_ACTION,
  REVIEW_RATING_FILTER_LABEL_KEYS,
  REVIEW_RATING_FILTER_ORDER,
  REVIEWS_DEFAULT_FILTERS
} from '../../consts';
import { usePicksOnClose, type ReviewsToolbarTracking } from '../../hooks';
import type { ReviewsFilters } from '../../types';
import { parseRatingPicks } from '../../utils';

const PERIODS = Object.values(REVIEW_PERIOD);
const SEARCH_DATA_HOOK = 'review-manager-reviews-search';

/** Focus moves into the toolbar, for a button that clears the search or the filters and so unmounts itself. */
export interface ReviewsToolbarFocus {
  /** Focuses the search box. */
  focusSearch: () => void;
  /** Focuses the first filter that is on; call it before the filters are cleared. */
  focusFirstFilterOn: () => void;
}

interface Props {
  /** The reviews table; its search box sends the search through the table. */
  table: TanStackTable<RMReview>;
  /** The search and filters the reviews are listed by. */
  filters: ReviewsFilters;
  /** The location's review sites; none hides the Source filter. */
  siteList: readonly RMAvailableDirectory[];
  /** Shows Clear all. */
  isClearAllShown: boolean;
  /** The reviews are loading for a new search: the search box shows it is busy. */
  isSearching: boolean;
  /** Called with the filter that changed. */
  onChange: (change: Partial<ReviewsFilters>) => void;
  /** Puts every filter back to its default and keeps the search. */
  onClearAll: () => void;
  /** Tracks the filter changes the user makes. */
  tracking: ReviewsToolbarTracking;
  /** Lets the card move focus to a toolbar control. */
  ref?: Ref<ReviewsToolbarFocus>;
}

/**
 * The reviews toolbar: the search, Source, Rating, Time period and Clear all. Source and Rating apply their picks
 * when their menu closes; the time period applies when picked.
 */
export const ReviewsToolbar = ({
  ref,
  table,
  filters,
  siteList,
  isClearAllShown,
  isSearching,
  onChange,
  onClearAll,
  tracking: { trackFilterChanged, trackPeriodChosen }
}: Props) => {
  const { t } = useTranslation();
  const handleSitesApply = (sites: string[]) => {
    trackFilterChanged({ filter: REVIEW_MANAGER_FILTER_ACTION.SOURCE, values: sites });
    onChange({ sites });
  };
  const handleRatingsApply = (values: string[]) => {
    trackFilterChanged({ filter: REVIEW_MANAGER_FILTER_ACTION.RATING, values });
    onChange({ ratings: parseRatingPicks(values) });
  };
  const handlePeriodChange = (period: ReviewPeriod) => {
    const isPeriodChanged = period !== filters.period;

    if (isPeriodChanged) {
      trackPeriodChosen(period);
    }
    onChange({ period });
  };
  const {
    picks: sitePicks,
    handlePicksChange: handleSitePicksChange,
    handleOpenChange: handleSitesOpenChange
  } = usePicksOnClose({ value: filters.sites, onApply: handleSitesApply });
  const {
    picks: ratingPicks,
    handlePicksChange: handleRatingPicksChange,
    handleOpenChange: handleRatingsOpenChange
  } = usePicksOnClose({ value: filters.ratings.map(String), onApply: handleRatingsApply });

  const siteOptions = siteList.map(({ identifier, name }) => ({ value: identifier, label: name }));
  const ratingOptions = REVIEW_RATING_FILTER_ORDER.map((rating) => ({
    value: String(rating),
    label: t(REVIEW_RATING_FILTER_LABEL_KEYS[rating])
  }));
  const periodOptions = PERIODS.map((period) => ({ value: period, label: t(PERIOD_LABEL_KEYS[period]) }));
  const hasSites = siteOptions.length > 0;
  const triggerSize = useFilterTriggerSize();

  const toolbarRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLButtonElement>(null);
  const ratingRef = useRef<HTMLButtonElement>(null);
  const periodRef = useRef<HTMLButtonElement>(null);
  const filterTriggers = [
    { isOn: filters.sites.length > 0, ref: sourceRef },
    { isOn: filters.ratings.length > 0, ref: ratingRef },
    { isOn: filters.period !== REVIEWS_DEFAULT_FILTERS.period, ref: periodRef }
  ];

  useImperativeHandle(ref, () => ({
    // TODO: DataTableSearch gives no ref to its input, so the search box is found under its data hook
    // [https://brightlocal.atlassian.net/browse/DS-1232]
    focusSearch: () =>
      toolbarRef.current?.querySelector<HTMLInputElement>(`[data-hook="${SEARCH_DATA_HOOK}"] input`)?.focus(),
    focusFirstFilterOn: () => filterTriggers.find(({ isOn }) => isOn)?.ref.current?.focus()
  }));

  return (
    <CardToolbar ref={toolbarRef} dataHook="review-manager-reviews-toolbar">
      <DataTableSearch<RMReview>
        ariaLabel={t('reports.reviewManager.filters.search')}
        clearSearchAriaLabel={t('reviewFilters.clearSearch')}
        dataHook={SEARCH_DATA_HOOK}
        loading={isSearching}
        placeholder={t('reports.reviewManager.filters.search')}
        table={table}
      />
      {hasSites && (
        <MultiSelectFilter
          ref={sourceRef}
          allLabel={t('reviewFilters.source.allLabel')}
          clearLabel={t('reviewFilters.clearFilters')}
          clearSearchAriaLabel={t('reviewFilters.clearSearch')}
          dataHook="review-manager-reviews-sites"
          doneLabel={t('reviewFilters.done')}
          drawerDescription={t('reports.reviewManager.filters.multiSelectDrawerDescription')}
          emptyDescription={t('reviewFilters.tryAnotherSearch')}
          emptyLabel={t('reports.reviewManager.filters.noSourceMatches')}
          label={t('reviewFilters.source.label')}
          options={siteOptions}
          presentation="auto"
          searchPlaceholder={t('reports.reviewManager.filters.searchSources')}
          size={triggerSize}
          summaryLabel={t('reviewFilters.selectedSummary')}
          value={sitePicks}
          onOpenChange={handleSitesOpenChange}
          onValueChange={handleSitePicksChange}
        />
      )}
      <MultiSelectFilter
        ref={ratingRef}
        allLabel={t('reviewFilters.rating.allLabel')}
        clearLabel={t('reviewFilters.clearFilters')}
        dataHook="review-manager-reviews-ratings"
        doneLabel={t('reviewFilters.done')}
        drawerDescription={t('reports.reviewManager.filters.multiSelectDrawerDescription')}
        label={t('reviewFilters.rating.label')}
        options={ratingOptions}
        presentation="auto"
        size={triggerSize}
        summaryLabel={t('reviewFilters.selectedSummary')}
        value={ratingPicks}
        onOpenChange={handleRatingsOpenChange}
        onValueChange={handleRatingPicksChange}
      />
      <SingleSelectFilter
        ref={periodRef}
        dataHook="review-manager-reviews-period"
        defaultValue={REVIEWS_DEFAULT_FILTERS.period}
        label={t('reviewFilters.period')}
        options={periodOptions}
        value={filters.period}
        onChange={handlePeriodChange}
      />
      {isClearAllShown && (
        <Button dataHook="review-manager-reviews-clear-filters" size="sm" variant="ghost" onClick={onClearAll}>
          {t('reports.reviewManager.filters.clearAll')}
        </Button>
      )}
    </CardToolbar>
  );
};
